<template>
  <div class="tarea-modal-wrapper">
    <v-dialog
      v-model="$store.consultasStore.state.currentTarea"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : 1400"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
      content-class="tarea-dialog"
    >
      <!-- Loading state -->
      <v-card v-if="$store.consultasStore.state.currentTarea.loading" class="modal-card loading-card">
        <div class="loading-header">
          <div class="modal-brand">
            <span class="brand-icon-dark"><v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon></span>
            <span class="brand-label-dark">Cargando {{ detailLabel }}</span>
          </div>
          <button class="close-btn-dark" @click="closeModal">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>
        <div class="loading-body">
          <v-progress-circular :size="40" :width="3" color="#3B5FDB" indeterminate />
          <p class="loading-text">Cargando información…</p>
        </div>
      </v-card>

      <!-- Main card -->
      <v-card v-else class="modal-card">

        <!-- ── TOP BAR ── -->
        <div class="topbar" ref="topbar">
          <div class="topbar-left">
            <span class="topbar-icon"><v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon></span>
            <div class="topbar-titles">
              <p class="mb-0 caption">{{ detailLabel }} <span class="caption font-weight-bold topbar-number">Nº{{ $store.consultasStore.state.currentTarea.number }}</span></p>
              <h2 class="topbar-title">
                {{ $store.consultasStore.state.currentTarea.title }}
              </h2>
              <p class="topbar-rdi" v-if="$store.consultasStore.state.currentTarea.rdi_relation && $store.consultasStore.state.currentTarea.rdi_relation.rdi">
                RDI asociada #{{ $store.consultasStore.state.currentTarea.rdi_relation.rdi.number }} —
                {{ $store.consultasStore.state.currentTarea.rdi_relation.rdi.title }}
                <button class="inline-link-btn"
                  @click="viewTarea($store.consultasStore.state.currentTarea.rdi_relation.rdi.id, $options.filters.seenByMe($store.consultasStore.state.currentTarea.rdi_relation.rdi.seen_by_me))">
                  <v-icon size="13" color="#5E85EF">mdi-arrow-right-bold</v-icon>
                </button>
              </p>
            </div>
          </div>
          <div class="topbar-right">
            <HeaderButtons :closeModal="closeModal" :draftId="draftId" />
          </div>
        </div>

        <!-- ── STATUS STRIP ── -->
        <div
                class="status-strip"
                :class="{ 'status-strip--sticky': statusStripSticky && $store.consultasStore.state.currentTarea.status != 3 }"
                :style="statusStripSticky && $store.consultasStore.state.currentTarea.status != 3 && $vuetify.breakpoint.mobile ? { top: topbarHeight + 'px' } : {}"
              >
          <div class="status-strip-pills">
            <span class="pill pill--blue">
              <v-icon size="12" color="#2D4B95">mdi-calendar-month-outline</v-icon>
              {{ $store.consultasStore.state.currentTarea.created_at | toDateTime }}
            </span>
            <span class="pill pill--green" v-if="$store.consultasStore.state.currentTarea.status == 3 && $store.consultasStore.state.currentTarea.ended_at">
              <v-icon size="12" color="#1B7A4E">mdi-check-circle-outline</v-icon>
              Finalizada {{ $store.consultasStore.state.currentTarea.ended_at | toDateTime }}
            </span>
            <span class="pill pill--orange" v-if="$store.consultasStore.state.currentTarea.critical_date">
              <v-icon size="12" color="#B45309">mdi-flag-outline</v-icon>
              Fecha crítica: {{ $store.consultasStore.state.currentTarea.critical_date | toDate }}
            </span>
            <span class="pill pill--gray">
              <v-icon size="12" color="#555">mdi-clock-time-eight-outline</v-icon>
              Espera: {{ $store.consultasStore.state.currentTarea.waitingtime }}
            </span>
            <span class="pill" :class="'pill--priority-' + getTareaPriorityKey($store.consultasStore.state.currentTarea)"
              v-if="$store.consultasStore.state.currentTarea.waiting_status != 1">
              <v-icon size="12">mdi-exclamation</v-icon>
              Prioridad: {{ getTareaPriorityText($store.consultasStore.state.currentTarea) }}
            </span>
            <span class="pill pill--private" v-if="$store.consultasStore.state.currentTarea.private">
              <v-icon size="12">mdi-incognito</v-icon>
              Privada
            </span>
            <span class="pill" :class="!$store.consultasStore.state.currentTarea.seen ? 'pill--unseen':'pill--seen'">
              <v-icon size="12">mdi-check</v-icon>
              {{ !$store.consultasStore.state.currentTarea.seen ? 'No visto por responsable' : 'Visto por responsable' }}
            </span>
						<div class="d-flex align-center" style="gap:5px">
							<v-divider vertical />
							<p class="mb-0 text-primary" style="font-size: 10px;">Visto por:</p>
							<SeenByUsers
								v-model="seenByMenu"
								:seenByUsers="currentTarea.seen_by || []"
							/>
						</div>
          </div>
          <div class="status-strip-actions">
            <div class="kanban-column-selector" v-if="currentTarea">
              <span class="kanban-column-label">
                <v-icon size="12" color="#2D4B95">mdi-view-column-outline</v-icon>
                Estado en Kanban
              </span>
              <v-select
                v-model="selectedKanbanColumn"
                :items="kanbanColumnOptions"
                :loading="loadingKanbanColumns || savingKanbanColumn"
                :disabled="!canChangeKanbanColumn || savingKanbanColumn"
                item-text="name"
                item-value="value"
                dense
                hide-details
                outlined
                color="#2D4B95"
                class="kanban-column-select"
                :menu-props="{ maxHeight: 280, contentClass: 'kanban-column-menu' }"
                @change="changeKanbanColumn"
              >
                <template v-slot:selection="{ item }">
                  <span class="kanban-column-option kanban-column-option--selection">
                    <span class="kanban-column-color" :style="{ backgroundColor: item.color }"></span>
                    <span class="kanban-column-name">{{ item.name }}</span>
                  </span>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    class="kanban-column-option kanban-column-option--item"
                    :class="{ 'kanban-column-option--active': item.value === selectedKanbanColumn }">
                    <span class="kanban-column-color" :style="{ backgroundColor: item.color }"></span>
                    <span class="kanban-column-name">{{ item.name }}</span>
                    <v-icon v-if="item.value === selectedKanbanColumn" size="16" color="#2D4B95">mdi-check</v-icon>
                  </v-list-item>
                </template>
              </v-select>
            </div>
						<BotSummarizeMini v-if="!isLoading" />
						<div class="d-flex align-center" style="gap:10px" v-if="isLoading">
							<v-progress-circular :size="16" :width="2" color="#3B5FDB" indeterminate />
						</div>
						<div class="task-closed-notice" v-else-if="$store.consultasStore.state.currentTarea.status == 3">
							<v-icon size="18" color="#1B7A4E">mdi-lock-check-outline</v-icon>
							<span>Esta tarea está cerrada.</span>
						</div>
            <TaskClosure v-else :task="currentTarea" @startCloseTasks="startCloseTasks" @reloadInfo="reloadTask" />
          </div>
        </div>

        <!-- ── TWO COLUMN BODY ── -->
        <div class="modal-body" :class="{ resizing: isResizing }">

          <!-- LEFT: detail panel -->
          <div class="panel-detail" :style="$vuetify.breakpoint.mobile ? {} : { width: leftPanelWidth + '%' }">
            <div class="panel-scroll">

              <!-- Meta grid -->
              <section class="meta-section">
                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Código</span>
                      <span class="meta-value">{{ $store.consultasStore.state.currentTarea.code ? $store.consultasStore.state.currentTarea.code.sku + ' — ' + $store.consultasStore.state.currentTarea.code.specialties : 'No asignado' }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Proyecto</span>
                      <span class="meta-value">{{ $store.consultasStore.state.currentTarea.project ? $store.consultasStore.state.currentTarea.project.name : 'No asignado' }}</span>
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item">
                      <span class="meta-label">Creador</span>
                      <span class="meta-value">{{ $store.consultasStore.state.currentTarea.created_by_user ? $store.consultasStore.state.currentTarea.created_by_user.name : 'No disponible' }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Responsable</span>
                      <div class="meta-value meta-responsible">
                        <span>{{ $store.consultasStore.state.currentTarea.responsable_user ? $store.consultasStore.state.currentTarea.responsable_user.name : 'Sin responsable' }}</span>
                        <v-menu offset-y v-if="checkPermissions(['change_responsable_task'])" @input="checkUsers(); searchUserResponsable = ''">
                          <template v-slot:activator="{ on, attrs }">
                            <button class="icon-action-btn" v-bind="attrs" v-on="on">
                              <v-icon size="14" color="#2D4B95">mdi-swap-horizontal-bold</v-icon>
                            </button>
                          </template>
                          <v-list dense class="py-0 responsible-menu">
                            <v-list-item dense style="min-height:32px; background:#2D4B95;">
                              <p class="caption mb-0 white--text font-weight-bold">Cambiar responsable</p>
                            </v-list-item>
                            <div class="search-responsible-wrapper" @click.stop>
                              <v-icon size="14" color="#999">mdi-magnify</v-icon>
                              <input 
                                v-model="searchUserResponsable" 
                                type="text" 
                                placeholder="Buscar usuario…"
                                class="search-responsible-input"
                                @click.stop
                              />
                            </div>
                            <div v-if="$store.usersStore.state.loadingUserListForItems" class="pa-3 text-center">
                              <v-progress-linear indeterminate color="#2D4B95" />
                            </div>
                            <v-list-item-group v-else color="#2D4B95" class="overflow-y-auto" style="max-height:260px">
                              <v-list-item 
                                v-for="(user, i) in $store.usersStore.state.usersListForItems.users.filter(u => {
                                  const search = searchUserResponsable.toLowerCase();
                                  return u.id !== $store.consultasStore.state.currentTarea.responsable && 
                                    (u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search));
                                })"
                                :key="i" dense class="px-0 py-0" @click="changeUser(user.id)">
                                <div style="width:100%">
                                  <div class="d-flex align-center px-3 py-2 gap-3">
                                    <img :src="user.info.avatar" class="user-avatar-sm" />
                                    <div>
                                      <p class="mb-0 caption font-weight-bold" style="color:#333">{{ user.name }}<span v-if="user.projectjob && user.projectjob.userjobtype"> — <span style="color:#2D4B95">{{ user.projectjob.userjobtype.name }}</span></span></p>
                                      <p class="mb-0 caption" style="color:#777">{{ user.email }}</p>
                                    </div>
                                  </div>
                                  <v-divider />
                                </div>
                              </v-list-item>
                            </v-list-item-group>
                          </v-list>
                        </v-menu>
                        <v-tooltip bottom v-else-if="userInfo.id != $store.consultasStore.state.currentTarea.responsable">
                          <template v-slot:activator="{ on, attrs }">
                            <button class="icon-action-btn" :style="attributionRequested ? 'opacity:.4':''"
                              v-bind="attrs" v-on="on" :disabled="attributionRequested" @click="requestTaskAttribution">
                              <v-icon size="14" color="#2D4B95">mdi-account-star-outline</v-icon>
                            </button>
                          </template>
                          <span>{{ attributionRequested ? 'Ya enviaste tu solicitud' : 'Solicitar ser responsable' }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </div>

                <v-divider class="meta-divider" />

                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item" v-if="$store.consultasStore.state.currentTarea">
                      <TaskParticipants
                        :tarea="$store.consultasStore.state.currentTarea"
                        :loading="loadingRemoveParticipant"
                        @openAddParticipant="addParticipantModal = true"
                        @removeParticipant="removeParticipant"
                      />
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item" v-if="$store.consultasStore.state.currentTarea && isCreator">
                      <TaskClosureProfileEdit
                        :tarea="$store.consultasStore.state.currentTarea"
                        @updated="onClosureProfileUpdated"
                      />
                    </div>

                    <div class="meta-item" v-if="checkPermissions(['change_responsable_task']) && currentTarea.attribution_requests && currentTarea.attribution_requests.length > 0">
                      <span class="meta-label">Solicitudes de responsable</span>
                      <div class="attribution-requests-list">
                        <div class="attribution-row" v-for="(req, i) in currentTarea.attribution_requests" :key="'req-'+i">
                          <span class="caption">{{ req.user ? req.user.name : 'Usuario no disponible' }}</span>
                          <div class="d-flex gap-2">
                            <button class="icon-action-btn success" @click="changeStatusTaskAttribution(2, req.user_id, req.id)">
                              <v-icon size="14" color="#1B7A4E">mdi-check</v-icon>
                            </button>
                            <button class="icon-action-btn danger" @click="changeStatusTaskAttribution(3, req.user_id, req.id)">
                              <v-icon size="14" color="#991B1B">mdi-close</v-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
								<div class="meta-item meta-item--association">
									<ItemsAssociationInput
										:item="$store.consultasStore.state.currentTarea"
										item_type="task"
                    :beforeNavigate="minimizeTask"
									/>
								</div>
              </section>

              <!-- Description -->
              <section class="content-section">
								<div class="d-flex justify-space-between align-center">
									<h3 class="section-title">Descripción</h3>
                  <button
                    v-if="showChecklist && !showNewSubtaskInput && $store.consultasStore.state.currentTarea.checklists.length === 0"
                    class="add-subtask-btn"
                    @click="openNewSubtaskInput">
                    <v-icon size="13" color="#6B7280">mdi-plus</v-icon>
                    Agregar subtarea
                  </button>
								</div>
                <div class="description-body" ref="descriptionBody" v-html="$store.consultasStore.state.currentTarea.description"></div>
                <MediaGallery :items="$store.consultasStore.state.currentTarea.attachments" />
                <GlobalFileViewer
                  :document-item="viewerDocumentItem"
                  :forge-item="viewerForgeItem"
                  @close="closeAttachmentViewer"
                  @minimize-file="minimizeAttachmentViewer"
                />

                <!-- ── Subtareas ── -->
                <!-- Sin subtareas: solo botón inline junto a título descripción -->
                <div v-if="showChecklist && $store.consultasStore.state.currentTarea.checklists.length === 0" class="subtasks-empty-row">
                  <div v-if="showNewSubtaskInput" class="new-subtask-input-wrap">
                    <textarea
                      ref="newSubtaskInput"
                      v-model="newSubtaskText"
                      class="new-subtask-input"
                      placeholder="Descripción de la subtarea… (Shift+Enter = nueva línea, Enter = agregar)"
                      rows="3"
                      @keydown.enter.exact.prevent="submitNewSubtask"
                      @keydown.esc="cancelNewSubtask"></textarea>
                    <div class="new-subtask-actions">
                      <button
                        class="send-btn"
                        style="padding:5px 12px;font-size:12px"
                        :disabled="!newSubtaskText.trim() || loadingNewSubtask"
                        @click="submitNewSubtask">
                        <v-progress-circular v-if="loadingNewSubtask" :size="12" :width="2" indeterminate color="white" />
                        <v-icon v-else size="13" color="white">mdi-check</v-icon>
                        Agregar
                      </button>
                      <button class="text-action-btn" @click="cancelNewSubtask">
                        <v-icon size="13" color="#666">mdi-close</v-icon>
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Con subtareas: bloque completo -->
                <div v-else-if="showChecklist" class="subtasks-block">
                  <div class="subtasks-block-header">
                    <div class="subtasks-block-title-row">
                      <v-icon size="15" color="#2D4B95">mdi-format-list-checks</v-icon>
                      <span class="subtasks-block-title">Subtareas</span>
                      <span class="subtasks-count">
                        {{ completedSubtasksCount }}/{{ $store.consultasStore.state.currentTarea.checklists.length }}
                      </span>
                    </div>
                    <button
                      v-if="!showNewSubtaskInput"
                      class="add-subtask-btn"
                      @click="openNewSubtaskInput">
                      <v-icon size="13" color="#6B7280">mdi-plus</v-icon>
                      Agregar subtarea
                    </button>
                  </div>

                  <div class="subtasks-list">
                    <label
                      class="subtask-card"
                      v-for="(sub, i) in $store.consultasStore.state.currentTarea.checklists"
                      :key="i"
                      :class="{ 'subtask-completed': sub.completed }">
                      <div class="subtask-checkbox-wrapper">
                        <input
                          type="checkbox"
                          v-model="sub.completed"
                          @change="changedChecklist"
                          class="subtask-check" />
                        <span class="checkbox-custom"></span>
                      </div>
                      <span class="subtask-text">{{ sub.description }}</span>
                      <button
                        class="subtask-delete-btn"
                        @click.stop="removeChecklistItemHandler(sub.id)"
                        title="Eliminar">
                        <v-icon size="14" color="#999">mdi-close</v-icon>
                      </button>
                    </label>
                  </div>

                  <div v-if="showNewSubtaskInput" class="new-subtask-input-wrap">
                    <textarea
                      ref="newSubtaskInput"
                      v-model="newSubtaskText"
                      class="new-subtask-input"
                      placeholder="Descripción de la subtarea… (Shift+Enter = nueva línea, Enter = agregar)"
                      rows="3"
                      @keydown.enter.exact.prevent="submitNewSubtask"
                      @keydown.esc="cancelNewSubtask"></textarea>
                    <div class="new-subtask-actions">
                      <button
                        class="send-btn"
                        style="padding:5px 12px;font-size:12px"
                        :disabled="!newSubtaskText.trim() || loadingNewSubtask"
                        @click="submitNewSubtask">
                        <v-progress-circular v-if="loadingNewSubtask" :size="12" :width="2" indeterminate color="white" />
                        <v-icon v-else size="13" color="white">mdi-check</v-icon>
                        Agregar
                      </button>
                      <button class="text-action-btn" @click="cancelNewSubtask">
                        <v-icon size="13" color="#666">mdi-close</v-icon>
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

              </section>

              <!-- Attachments -->
              <section class="content-section">
                <div class="section-header-row">
                  <h3 class="section-title">Adjuntos</h3>
                  <button class="text-action-btn"
                    v-if="$store.consultasStore.state.currentTarea.attachments.length > 1"
                    :disabled="loadingFilePath"
                    @click="downloadAttachments($store.consultasStore.state.currentTarea.attachments)">
                    <v-icon size="14" color="#3B5FDB">mdi-download-multiple</v-icon>
                    Descargar todo
                  </button>
                </div>
                <div class="attachments-list" v-if="$store.consultasStore.state.currentTarea.attachments.filter(item => !item.pathmedia).length > 0">
                  <div class="attachment-row" v-for="(att, i) in $store.consultasStore.state.currentTarea.attachments.filter(item => !item.pathmedia)" :key="i">
                    <div class="attachment-info">
                      <v-icon size="16" color="#3B5FDB">mdi-file-outline</v-icon>
                      <span class="attachment-name">{{ att.name }}</span>
                    </div>
                    <div class="attachment-actions">
                      <AttachmentPreviewButton
                        :attachment="att"
                        :loading="loadingFilePath"
                        compact
                        @preview="previewAttachment"
                      />
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true)">
                            <v-icon size="14" color="#3B5FDB">mdi-open-in-new</v-icon>
                          </button>
                        </template>
                        <span>Ver en navegador</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true, true)">
                            <v-icon size="14" color="#3B5FDB">mdi-cloud-download-outline</v-icon>
                          </button>
                        </template>
                        <span>Descargar</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
                <p class="empty-note" v-else>Sin adjuntos</p>
              </section>

            </div>
          </div>

          <!-- Resizer (desktop only) -->
          <div class="panel-resizer" @mousedown="startResize" v-if="!$vuetify.breakpoint.mobile">
            <div class="resize-handle"></div>
          </div>

          <!-- RIGHT: interactions panel -->
          <div class="panel-interactions" :class="{ 'panel-interactions--editor-open': showEditor }">
            <div class="interactions-header">
              <div class="d-flex align-center" style="gap: 15px;">
                <h3 class="section-title" style="margin:0">Interacción</h3>
                <div class="add-comment-section">
                  <button class="add-comment-btn" @click="toggleEditorWithScroll">
                    <v-icon size="16" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                    {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
                  </button>
                </div>
              </div>
              <div class="interactions-header-actions">
                <div class="search-wrap">
                  <v-icon size="15" color="#999">mdi-magnify</v-icon>
                  <input class="search-field" placeholder="Buscar…" v-model="interactionSearch" type="text" />
                </div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <button class="create-btn" v-bind="attrs" v-on="on">
                      <v-icon size="14" color="white">mdi-plus</v-icon>
                      Crear
                    </button>
                  </template>
                  <div class="create-menu">
                    <button class="create-menu-item" v-if="checkPermissions(['create_tasks'])" @click="sendToTask()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon> Enviar a tarea
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_tasks'])" @click="sendToAdditionalLikely()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon> Posible adicional
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_rdi'])" @click="sendToRDI()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon> Enviar a RDI
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_additionals'])" @click="sendToAdditional()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon> Enviar a adicional
                    </button>
                  </div>
                </v-menu>
              </div>
            </div>

            <div class="interactions-feed" v-if="$store.consultasStore.state.currentTarea.interactions.length > 0">
              <Interactions
                itemType="tasks_restrictions_interactions"
                :interactions="filteredInteractions"
                :searchText="interactionSearch"
                :item="currentTarea"
                @reload="reloadTask"
                @reply-interaction="onReplyInteraction"
								:beforeNavigate="minimizeTask"
              />
              <!-- Mobile: Add comment button at the end -->
              <div class="mobile-add-comment-footer" v-if="$vuetify.breakpoint.mobile && $store.consultasStore.state.currentTarea.status != 3 && checkInteractPermission(['interact_tasks'])">
                <button class="mobile-add-comment-btn" @click="toggleEditorWithScroll">
                  <v-icon size="18" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                  {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
                </button>
              </div>
            </div>
            <div class="empty-interactions" v-else>
              <v-icon size="36" color="#ddd">mdi-chat-outline</v-icon>
              <p>Sin interacciones aún</p>
              <!-- Mobile: Add comment button when empty -->
              <button class="mobile-add-comment-btn" v-if="$vuetify.breakpoint.mobile && $store.consultasStore.state.currentTarea.status != 3 && checkInteractPermission(['interact_tasks'])" @click="toggleEditorWithScroll">
                <v-icon size="18" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
              </button>
            </div>

            <div class="compose-area" v-if="$store.consultasStore.state.currentTarea.status != 3 && checkInteractPermission(['interact_tasks'])" ref="composeArea">
              <div v-if="showEditor">
                <div class="reply-banner" v-if="interactionParent">
                  <div class="d-flex align-center gap-2">
                    <v-icon size="14" color="#2D4B95">mdi-reply</v-icon>
                    <div>
                      <p class="mb-0 caption font-weight-bold" style="color:#2D4B95">Respondiendo a {{ interactionParent.user ? interactionParent.user.name : '' }}</p>
                      <div v-html="interactionParent.message" class="reply-preview"></div>
                    </div>
                  </div>
                  <button class="icon-action-btn" @click="clearReply">
                    <v-icon size="13">mdi-close</v-icon>
                  </button>
                </div>

                <v-dialog v-model="replyModeDialog" max-width="400" persistent>
                  <v-card class="pa-4">
                    <h3 class="mb-2" style="color:#2D4B95;font-size:15px">¿Cómo mostrar tu respuesta?</h3>
                    <v-radio-group v-model="replyShowInFeed" hide-details>
                      <v-radio :value="true" color="#3B5FDB">
                        <template v-slot:label>
                          <div>
                            <p class="mb-0 body-2 font-weight-medium">Como mensaje nuevo al final</p>
                            <p class="mb-0 caption" style="color:#888">Aparece al final indicando a qué responde</p>
                          </div>
                        </template>
                      </v-radio>
                      <v-divider class="my-2"/>
                      <v-radio :value="false" color="#3B5FDB">
                        <template v-slot:label>
                          <div>
                            <p class="mb-0 body-2 font-weight-medium">Solo como subrespuesta</p>
                            <p class="mb-0 caption" style="color:#888">Anidada dentro del mensaje original</p>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                    <div class="d-flex justify-end gap-2 mt-4">
                      <button class="text-action-btn" @click="cancelReplyMode">Cancelar</button>
                      <button class="create-btn" @click="confirmReplyMode">Confirmar</button>
                    </div>
                  </v-card>
                </v-dialog>

                <InteractionEditor
									v-model="message"
									item_type="task"
									:item="currentTarea"
									@paste-image="onPasteImage"
									@mentions-updated="onMentionsUpdated" />

                <div class="response-alert" v-if="is_response">
                  <v-icon size="15" color="#B45309">mdi-alert-outline</v-icon>
                  <span>Estás respondiendo de forma oficial.</span>
                  <button class="inline-link" @click="() => { is_response = false; response_type = 0 }">Cambiar a interacción</button>
                </div>

                <div v-if="filesPreview.length > 0" class="attachments-preview">
                  <button class="attach-toggle" @click="showAttachments = !showAttachments">
                    <span class="attach-dot"></span>
                    {{ filesPreview.length }} {{ filesPreview.length === 1 ? 'adjunto' : 'adjuntos' }}
                    <v-icon size="14" :class="['chevron', { open: showAttachments }]">mdi-chevron-down</v-icon>
                  </button>
                  <div v-if="showAttachments" class="attach-grid">
                    <div class="img-chip" v-for="(f, i) in filesPreview.filter(f => f.src)" :key="'img-'+i">
                      <img :src="f.src" class="img-thumb" />
                      <button class="chip-remove" @click="removeAttachment(f, i)"><v-icon x-small color="white">mdi-close</v-icon></button>
                    </div>
                    <div class="file-chip" v-for="(f, i) in filesPreview.filter(f => !f.src)" :key="'file-'+i">
                      <v-icon small color="#2D4B95">mdi-file-outline</v-icon>
                      <span class="file-name">{{ f.name }}</span>
                      <button class="chip-remove file" @click="removeAttachment(f, i)"><v-icon x-small>mdi-close</v-icon></button>
                    </div>
                  </div>
                </div>

                <div class="compose-actions mt-1">
                  <button class="send-btn" :disabled="loading" @click="preAddInteraction()">
                    <v-progress-circular v-if="loading" :size="14" :width="2" indeterminate color="white" />
                    <v-icon v-else size="15" color="white">mdi-send</v-icon>
                    {{ confirmButtonText }}
                  </button>
                  <div class="compose-secondary">
                    <button class="text-action-btn" @click="onButtonClick">
                      <v-icon size="15" color="#2D4B95">mdi-paperclip</v-icon>
                      Adjuntar
                    </button>
                    <button class="text-action-btn" @click="showEditor = false; clearEditorContent()">
                      <v-icon size="15" color="#666">mdi-close</v-icon>
                      Cancelar
                    </button>
                    <input ref="uploader" multiple class="d-none" type="file" @change="onFilesChanged" />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </v-card>
    </v-dialog>

    <div v-if="isResizing" class="resize-overlay"></div>

    <AcceptCancelModal :acceptRejectModal="acceptRejectModal" :acceptRejectModalData="acceptRejectModalData" :closeModal="closeModalAcceptCancel" />
    <AddParticipantModal :item="currentTarea" :addParticipantModal="addParticipantModal" :closeModal="closeAddParticipantModal" />
    <ChangeResponsablePromptModal
      :changeResponsablePromptModal="changeResponsablePromptModal"
      :previousResponsableName="previousResponsableName"
      :nextResponsableName="nextResponsableName"
      :loading="loadingChangeUser"
      :closeModal="cancelChangeResponsablePrompt"
      :onlyChange="changeResponsableOnly"
      :keepParticipant="changeResponsableKeepingParticipant"
    />
  </div>
</template>

<script>
import moment from 'moment'
import _debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid'
import EventBus from '@/helpers/event_bus'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal'
import AddParticipantModal from '@/components/Globals/Modals/General/AddParticipantModal'
import ChangeResponsablePromptModal from '@/components/Globals/Modals/General/ChangeResponsablePromptModal'
import { getCapabilitiesByUserProject } from '@/helpers/api/user'
import {addInteraction, getTareaByID, getPublicUrl, closeTask, updateTask, changeResponsable, removeParticipant, addParticipant, requestTaskAttribution, changeTaskAttributionRequest, addChecklistItem, removeChecklistItem} from '@/helpers/api/tasks'
import {getRdiByID} from '@/helpers/api/rdi'
import {getZippedFiles} from '@/helpers/api/fileUploads'
import {deleteFile} from '@/helpers/api/fileManager'
import {create as createDraftAPI, update as updateDraftAPI} from '@/helpers/api/drafts'
import MediaGallery from '@/components/Globals/Helpers/MediaGallery'
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import AttachmentPreviewButton from '@/components/Globals/Helpers/AttachmentPreviewButton'
import Interactions from '@/components/Globals/Helpers/Interactions'
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import HeaderButtons from '@/components/Globals/Modals/Tasks/Parts/HeaderButtons'
import ItemsAssociationInput from '@/components/Globals/Helpers/ItemsAssociationInput'
import TaskClosure from '@/components/Views/Consultas/Parts/TaskClosure'
import TaskParticipants from '@/components/Views/Consultas/Parts/TaskParticipants'
import TaskClosureProfileEdit from '@/components/Views/Consultas/Parts/TaskClosureProfileEdit'
import BotSummarizeMini from '@/components/Globals/Helpers/BotSummarizeMini'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'
import { assocClickMixin } from '@/helpers/assocClickMixin'

export default {
  name: 'TareaModal',
  props: {
    isPossibleAdditional: {
      type: Boolean,
      default: undefined
    }
  },
  mixins: [assocClickMixin],
  components: { AcceptCancelModal, AddParticipantModal, ChangeResponsablePromptModal, MediaGallery, GlobalFileViewer, AttachmentPreviewButton, HeaderButtons, Interactions, InteractionEditor, ItemsAssociationInput, TaskClosure, TaskParticipants, TaskClosureProfileEdit, BotSummarizeMini, SeenByUsers },
  computed: {
		isCreator(){
			return this.userInfo.id === this.currentTarea.created_by
		},
    isPossibleAdditionalMode() {
      return this.isPossibleAdditional === undefined
        ? Boolean(this.currentTarea && this.currentTarea.is_posible_additional)
        : this.isPossibleAdditional
    },
    detailLabel() {
      return this.isPossibleAdditionalMode ? 'Posible adicional' : 'Tarea'
    },
    draftType() {
      return this.isPossibleAdditionalMode ? 8 : 5
    },
    showChecklist() {
      return !this.isPossibleAdditionalMode
    },
    isUserPart() {
      if (!this.$store.userStore.state.userInfo || !this.$store.consultasStore.state.currentTarea) return false
      const uid = this.$store.userStore.state.userInfo.id
      const t = this.$store.consultasStore.state.currentTarea
      return (t.created_by_user && uid === t.created_by_user.id) ||
        (t.responsable_user && uid === t.responsable_user.id) ||
        (t.participants && t.participants.some(p => Number(p.user_id || p.id) === Number(uid)))
    },
    currentTarea() { return this.$store.consultasStore.state.currentTarea },
    attributionRequested() {
      if (this.currentTarea.attribution_requests) {
        return this.currentTarea.attribution_requests.some(a => this.userInfo.id == a.user_id)
      }
      return false
    },
    userInfo() { return this.$store.userStore.state.userInfo },
    previousResponsableName() {
      return this.currentTarea?.responsable_user?.name || 'responsable actual'
    },
    nextResponsableName() {
      if (!this.pendingResponsableUserID) return 'nuevo responsable'
      const users = this.$store.usersStore.state.usersListForItems?.users || []
      return users.find(u => u.id === this.pendingResponsableUserID)?.name || 'nuevo responsable'
    },
    draftToLoad() { return this.$store.consultasStore.state.draftToLoad },
    filteredInteractions() {
      const items = this.$store.consultasStore.state.currentTarea.interactions
      if (this.interactionSearch && items) {
        return items.filter(i => i.message.toLowerCase().includes(this.interactionSearch.toLowerCase()))
      }
      return items ? [...items] : []
    },
    completedSubtasksCount() {
      if (!this.$store.consultasStore.state.currentTarea.checklists) return 0
      return this.$store.consultasStore.state.currentTarea.checklists.filter(c => c.completed).length
    },
    kanbanColumns() {
      const projectId = this.currentTarea && this.currentTarea.project_id
      return projectId ? (this.$store.kanbanStore.state.columnsByProject[projectId] || []) : []
    },
    kanbanColumnOptions() {
      return [
        { value: '__backlog__', name: 'Backlog', color: '#607D8B' },
        ...this.kanbanColumns.map(column => ({
          value: String(column.id),
          name: column.name || 'Sin nombre',
          color: column.color || '#5E85EF'
        }))
      ]
    },
    canChangeKanbanColumn() {
      if (!this.currentTarea || !this.userInfo) return false
      return Number(this.userInfo.id) === Number(this.currentTarea.created_by) ||
        Number(this.userInfo.id) === Number(this.currentTarea.responsable) || this.checkPermissions(['full_change_kanban'])
    }
  },
  watch: {
    currentTarea(newData) {
      if (newData && !newData.loading && newData.id) {
				this.updateTopbarHeight()
				this.loadKanbanColumns(newData)
        setTimeout(() => { this.pasteListener() }, 500)
        this.$nextTick(() => {
          this.$_unbindAssocClick(this._descBodyEl)
          this._descBodyEl = this.$refs.descriptionBody
          this.$_bindAssocClick(this._descBodyEl)
        })
      }
    },
    message(val) { if (val && val.length > 0) this.handleFormChangeDebounced() },
    files() { this.handleFormChangeDebounced() }
  },
  data: () => ({
		topbarHeight: 0,
		isLoading: false,
    statusStripSticky: false,
    scrollContainer: null,
    statusScrollables: [],
    dialog: true,
    showMore: false,
    interactionSearch: '',
    searchUserResponsable: '',
    showAttachments: true,
    message: '',
    response_type: 0,
    need_reload: false,
    showEditor: false,
    leftPanelWidth: 50,
    isResizing: false,
    files: [],
    filesPreview: [],
    loadingFilePath: false,
    is_response: false,
    loading: false,
    changeInChecklists: false,
    loadingChangeUser: false,
    loadingTaskAttribution: false,
    acceptRejectModal: false,
    acceptRejectModalData: null,
    acceptRejectModalContext: null,
    addParticipantModal: false,
    loadingRemoveParticipant: false,
    loadingEdit: false,
    cancelTimer: 4,
    inCancelTime: false,
    cancelInterval: null,
    confirmButtonText: 'Enviar',
    changeResponsablePromptModal: false,
    pendingResponsableUserID: null,
    draftId: null,
    draftCreated: false,
    draftUpdateInterval: 20,
    draftSaveTimer: null,
    draftCreationPromise: null,
    handleFormChangeDebounced: null,
    handleChecklistChangeDebounced: null,
    isResetting: false,
    interactionParent: null,
    replyModeDialog: false,
    replyShowInFeed: true,
    newInteractionMentions: [],
    pendingMentionParticipants: [],
    showNewSubtaskInput: false,
    newSubtaskText: '',
    loadingNewSubtask: false,
		seenByMenu: false,
    selectedKanbanColumn: '__backlog__',
    loadingKanbanColumns: false,
    savingKanbanColumn: false,
    viewerDocumentItem: null,
    viewerForgeItem: null
  }),
  mounted() {
    this.setListeners()
    this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
    this.handleChecklistChangeDebounced = _debounce(() => this.saveChecklistChanges(), 800)
    if (this.draftToLoad) { this.loadDraft(this.draftToLoad) }
		if (this.currentTarea && !this.currentTarea.loading) this.loadKanbanColumns(this.currentTarea)
    window.addEventListener('mousemove', this.handleResize)
    window.addEventListener('mouseup', this.stopResize)
		this.updateTopbarHeight()
    window.addEventListener('resize', this.updateTopbarHeight)
  },
  beforeDestroy() {
    EventBus.$off('completedFileUpload')
		EventBus.$off('reload_element')
    if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
    if (this.cancelInterval) { clearInterval(this.cancelInterval); this.cancelInterval = null }
    window.removeEventListener('mousemove', this.handleResize)
    window.removeEventListener('mouseup', this.stopResize)
    this.$_unbindAssocClick(this._descBodyEl)
    window.removeEventListener('resize', this.updateTopbarHeight)
    this.unbindStatusStripScroll()
  },
  methods: {
    async loadKanbanColumns(task) {
      if (!task || task.loading || !task.project_id) return
      this.selectedKanbanColumn = task.kanban_column && task.kanban_column.kanban_project_col_id
        ? String(task.kanban_column.kanban_project_col_id)
        : '__backlog__'
      this.loadingKanbanColumns = true
      try {
        await this.$store.kanbanStore.dispatch('getProjectColumns', { project_id: task.project_id })
      } catch (error) {
        this.$store.globalStore.dispatch('addMessage', {
          message: error.message || 'No fue posible cargar las columnas Kanban.',
          title: 'Error', created: new Date(), type: 'error',
          icon: 'mdi-alert-circle', color: '#F4516C'
        })
      } finally {
        this.loadingKanbanColumns = false
      }
    },
    async changeKanbanColumn(value) {
      if (!this.canChangeKanbanColumn || this.savingKanbanColumn || !this.currentTarea) return
      const previousValue = this.currentTarea.kanban_column && this.currentTarea.kanban_column.kanban_project_col_id
        ? String(this.currentTarea.kanban_column.kanban_project_col_id)
        : '__backlog__'
      if (value === previousValue) return

      this.savingKanbanColumn = true
      try {
        const response = await this.$store.kanbanStore.dispatch('updateTaskColumn', {
          task_id: this.currentTarea.id,
          kanban_project_col_id: value === '__backlog__' ? null : Number(value)
        })
        if (response.code !== 200) throw new Error(response.message || 'No fue posible actualizar la columna.')

        this.$store.consultasStore.commit('setCurrentTarea', {
          ...this.currentTarea,
          kanban_column: response.kanban_column || null
        })
        EventBus.$emit('kanbanTaskColumnChanged', {
          task_id: this.currentTarea.id,
          project_id: this.currentTarea.project_id,
          kanban_column: response.kanban_column || null
        })
        this.$store.globalStore.dispatch('addMessage', {
          message: value === '__backlog__' ? 'Tarea enviada al Backlog.' : 'Columna Kanban actualizada.',
          title: 'Tarea actualizada', created: new Date(), type: 'success',
          icon: 'mdi-check-circle-outline', color: '#34BFA3'
        })
      } catch (error) {
        this.selectedKanbanColumn = previousValue
        this.$store.globalStore.dispatch('addMessage', {
          message: error.message || 'No fue posible actualizar la columna Kanban.',
          title: 'Error', created: new Date(), type: 'error',
          icon: 'mdi-alert-circle', color: '#F4516C'
        })
      } finally {
        this.savingKanbanColumn = false
      }
    },
    updateTopbarHeight() {
      this.$nextTick(() => {
        if (this.$refs.topbar) {
          this.topbarHeight = this.$refs.topbar.offsetHeight
        }
        this.bindStatusStripScroll()
      })
    },
    async beforeAssocNavigate () {
      return this.minimizeTask()
    },

    toggleEditorWithScroll() {
      this.showEditor = !this.showEditor
      if (this.showEditor) {
        // Esperar a que se despliegue completamente la sección
        setTimeout(() => {
          if (this.$refs.composeArea) {
            this.$refs.composeArea.scrollIntoView({ behavior: 'smooth', block: 'end' })
          }
        }, 300)
      }
    },
    onMentionsUpdated(userIds) {
			this.newInteractionMentions = userIds
		},
    onReplyInteraction(interaction) {
			this.interactionParent = interaction;
			this.replyModeDialog = true
			this.toggleEditorWithScroll()
		},
    confirmReplyMode() { this.replyModeDialog = false },
    cancelReplyMode() { this.replyModeDialog = false; this.interactionParent = null; this.replyShowInFeed = true },
    clearReply() { this.interactionParent = null; this.replyShowInFeed = true },
    checkUsers() {
      if (!this.$store.usersStore.state.loadingUserListForItems &&
        this.$store.usersStore.state.usersListForItems.project_id !== this.$store.consultasStore.state.currentTarea.project_id) {
        this.getUserListforItems()
      }
    },
    getUserListforItems() {
      this.$store.usersStore.dispatch('getUsersListForItems', {
        project_id: this.$store.consultasStore.state.currentTarea.project_id,
        start: 0, limit: 9999
      })
    },
    getTareaPriorityKey(tarea) {
      if (tarea.priority === 3) return 'high'
      if (tarea.priority === 2) return 'medium'
      return 'low'
    },
    getTareaPriorityText(tarea) {
      if (tarea.priority === 3) return 'Inmediata'
      if (tarea.priority === 2) return 'Media'
      return 'Estándar'
    },
    checkIfNearDeadline(tarea) {
      if (!tarea.critical_date) return false
      return moment(tarea.critical_date).diff(moment(), 'days') <= 2
    },
    async onPasteImage(file) { await this.createDraft(true); this.onFileChanged(null, file) },
    checkInteractPermission(permissionToCheck) {
      if (!this.checkPermissions(permissionToCheck)) return false
      return this.checkPermissions(['interact_tasks_others']) || this.isUserPart
    },
    pasteListener() {
      const el = document.querySelector('#interaction-texteditor .ProseMirror')
      if (el) el.addEventListener('paste', this.pasteListenerHandler)
    },
    async pasteListenerHandler(event) {
      const file = (event.clipboardData || window.clipboardData).files[0]
      if (file) this.onFileChanged(null, file)
    },
    changedChecklist() {
      if (!this.showChecklist) return
      this.changeInChecklists = true
      this.handleChecklistChangeDebounced()
    },
    async saveChecklistChanges() {
      if (this.showChecklist && this.$store.consultasStore.state.currentTarea) {
        await updateTask({ ...this.$store.consultasStore.state.currentTarea })
        this.changeInChecklists = false
      }
    },
    async removeChecklistItemHandler(checklistId) {
      if (!this.showChecklist) return
      const checklistArray = this.$store.consultasStore.state.currentTarea.checklists
      const itemIndex = checklistArray.findIndex(item => item.id === checklistId)
      
      if (itemIndex === -1) return
      
      const removedItem = checklistArray[itemIndex]
      checklistArray.splice(itemIndex, 1)
      
      try {
        await removeChecklistItem({ checklist_item_id: checklistId })
      } catch (error) {
        checklistArray.splice(itemIndex, 0, removedItem)
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Error al eliminar la subtarea',
          title: 'Error',
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#F4516C'
        })
      }
    },
    async updateTaskInfo(taskInfo) { await updateTask(taskInfo) },
    setListeners() {
      EventBus.$on('completedFileUpload', async (payload) => {
        if (payload && payload.draft_id === this.draftId) {
          const idx = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
          if (idx !== -1) this.$set(this.filesPreview[idx], 'id', payload.file.id)
        } else if (payload && payload.task_id && this.$store.consultasStore.state.currentTarea && payload.task_id == this.$store.consultasStore.state.currentTarea.id) {
          const tarea = await getTareaByID({ task_id: payload.task_id })
          if (tarea.code === 200) this.$store.consultasStore.commit('setCurrentTarea', tarea.task)
        }
      })
      EventBus.$on('reload_element', async (payload) => {
        if (payload?.type == 'task' && payload?.id == this.$store.consultasStore.state.currentTarea?.id) {
          const tarea = await getTareaByID({ task_id: payload?.id })
          if (tarea.code === 200) this.$store.consultasStore.commit('setCurrentTarea', tarea.task)
        }
      })
    },
    handleFormChange() { if (!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced() },
    checkAndCreateDraft() { if (!this.draftCreated && !this.draftId) this.createDraft() },
    async createDraft(force_create = false) {
      if (this.draftId) return this.draftId
      if (this.draftCreated) return this.draftCreationPromise
      if ((!this.message || this.message.trim() === '') && !force_create) return null
      this.draftCreated = true
      const draftData = {
        type: this.draftType,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.$store.consultasStore.state.currentTarea.id,
        data: JSON.stringify({
          message: this.message,
          title: this.$store.consultasStore.state.currentTarea.title,
          number: this.$store.consultasStore.state.currentTarea.number
        })
      }
      try {
        this.draftCreationPromise = createDraftAPI(draftData)
        const response = await this.draftCreationPromise
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
          EventBus.$emit('reloadDraftList')
          return this.draftId
        }
        return null
      } catch (error) { this.draftCreated = false; throw error
      } finally { this.draftCreationPromise = null }
    },
    startDraftUpdateTimer() {
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.draftSaveTimer = setInterval(() => { this.updateDraft() }, this.draftUpdateInterval * 1000)
    },
    async updateDraft() {
      if (!this.draftId) return
      await updateDraftAPI({
        id: this.draftId, type: this.draftType,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.$store.consultasStore.state.currentTarea.id,
        data: JSON.stringify({ message: this.message, title: this.$store.consultasStore.state.currentTarea.title, number: this.$store.consultasStore.state.currentTarea.number })
      })
    },
    async loadDraft(draft) {
      if (draft && draft.data) {
        this.isResetting = true
        this.message = draft.data.message || ''
        const filePreviews = []
        if (draft.files && Array.isArray(draft.files)) {
          for (const file of draft.files) {
            if (file.mime_type && file.mime_type.includes('image') && file.pathmedia?.path) {
              filePreviews.push({ src: file.pathmedia.path, id: file.id, name: file.name })
            } else {
              filePreviews.push({ noImage: true, id: file.id, name: file.name })
            }
          }
        }
        this.filesPreview = filePreviews
        this.draftId = draft.id
        this.draftCreated = true
        this.showEditor = true
        this.startDraftUpdateTimer()
        this.$nextTick(() => { this.isResetting = false })
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Borrador cargado correctamente.', title: 'Borrador Cargado',
          created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
        })
        this.$store.consultasStore.commit('setDraftToLoad', null)
      }
    },
    async closeModal() {
      if (!this.draftId && this.draftCreationPromise) await this.draftCreationPromise
      if (!this.draftId && this.message && this.message.trim()) await this.createDraft()
      if (this.draftId) await this.updateDraft()
      const savedDraftId = this.draftId
      if (this.showChecklist && this.changeInChecklists) this.updateTaskInfo({ ...this.$store.consultasStore.state.currentTarea })
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.$store.consultasStore.commit('setCurrentTarea', null)
      this.reloadListing()
      this.changeInChecklists = false; this.need_reload = false; this.loading = false
      this.files = []; this.filesPreview = []
      this.changeResponsablePromptModal = false; this.pendingResponsableUserID = null
      if (this.cancelInterval) { clearInterval(this.cancelInterval); this.cancelInterval = null }
      this.inCancelTime = false; this.acceptRejectModal = false
      this.acceptRejectModalContext = null; this.acceptRejectModalData = null
      this.pendingMentionParticipants = []
      this.draftId = null; this.draftCreated = false; this.message = ''
      this.showEditor = false
      this.showNewSubtaskInput = false; this.newSubtaskText = ''
      this.searchUserResponsable = ''
      EventBus.$emit('reloadDraftList')
      return savedDraftId
    },
    onButtonClick() { this.$refs.uploader.click() },
		async onFilesChanged(e) {
			await this.createDraft(true);
			const files = e?.target?.files || []
			for (let i = 0; i < files.length; i++) {
				this.onFileChanged(null, files[i])
			}
		},
    async onFileChanged(e, file = null) {
      const newFile = e?.target?.files[0] || file
      if (!newFile) return
      if (!this.draftId && !this.draftCreated) await this.createDraft(true)
      const uuid = uuidv4()
      if (newFile.type.includes('image')) {
        const b64 = await this.toBase64(newFile)
        this.filesPreview.push({ src: b64, name: newFile.name, uuid })
      } else {
        this.filesPreview.push({ noImage: true, name: newFile.name, uuid })
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type: 13,
        payload: { draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid },
        description: this.currentTarea.title, total: newFile.size, uuid, file: newFile
      })
    },
    removeAttachment(file, index) {
      this.filesPreview.splice(index, 1)
      if (file.id) deleteFile({ file_id: file.id })
    },
    toBase64(file) {
      return new Promise(resolve => {
        const r = new FileReader(); r.readAsDataURL(file)
        r.onload = () => resolve(r.result)
        r.onerror = () => resolve(null)
      })
    },
    async getFilesUrl(attachments, singleFile = false, download = false) {
      if (singleFile) attachments = [attachments]
      this.loadingFilePath = true
      const files = await getPublicUrl({ attachments, force_download: download })
      if (files.code === 200) files.files.forEach(f => window.open(f.url))
      this.loadingFilePath = false
    },
    previewAttachment (attachment) {
      this.viewerDocumentItem = null
      this.viewerForgeItem = null

      if (attachment?.pathdoc?.path) {
        this.viewerDocumentItem = attachment
        return
      }

      if (attachment?.forge_file?.translated) {
        this.viewerForgeItem = attachment
        return
      }

      this.$store.globalStore.dispatch('addMessage', {
        message: 'Este archivo no tiene un visor disponible.',
        title: 'Vista previa no disponible',
        created: new Date(),
        type: 'warning',
        icon: 'mdi-eye-off-outline',
        color: '#FB8500'
      })
    },
    closeAttachmentViewer () {
      this.viewerDocumentItem = null
      this.viewerForgeItem = null
    },
    minimizeAttachmentViewer (item) {
      this.$store.globalStore.commit('addFileToFileManagerHandler', item)
      this.closeAttachmentViewer()
    },
    openNewSubtaskInput() {
      this.showNewSubtaskInput = true
      this.$nextTick(() => {
        const input = this.$refs.newSubtaskInput
        if (input) input.focus()
      })
    },
    cancelNewSubtask() {
      this.showNewSubtaskInput = false
      this.newSubtaskText = ''
    },
    async submitNewSubtask() {
      if (!this.showChecklist) return
      const text = this.newSubtaskText || ''
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0)
      if (lines.length === 0 || this.loadingNewSubtask) return
      this.loadingNewSubtask = true
      this.newSubtaskText = ''
      try {
        if (lines.length === 1) {
          await addChecklistItem({ task_id: this.currentTarea.id, description: lines[0] })
        } else {
          await addChecklistItem({ task_id: this.currentTarea.id, descriptions: lines })
        }
        await this.reloadTask()
      } finally {
        this.loadingNewSubtask = false
        this.$nextTick(() => {
          const input = this.$refs.newSubtaskInput
          if (input) input.focus()
        })
      }
    },
    async addTaskChecklistItem(description) {
      if (!this.showChecklist) return
      const payload = Array.isArray(description) ? { task_id: this.currentTarea.id, descriptions: description } : { task_id: this.currentTarea.id, description }
      await addChecklistItem(payload)
      await this.reloadTask()
    },
    async preAddInteraction() {
      if (this.inCancelTime) {
        clearInterval(this.cancelInterval)
        this.confirmButtonText = 'Enviar'
        this.inCancelTime = false
        return null
      }
      if (!this.message || this.message === '') {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Por favor escribe un mensaje.', title: 'Error', created: new Date(),
          type: 'success', icon: 'mdi-alert-circle', color: '#fb8c00'
        })
        return false
      }

      this.pendingMentionParticipants = []
      if (this.newInteractionMentions.length > 0) {
        const capabilities = await getCapabilitiesByUserProject({
          project_id: this.$store.projectsStore.state.selectedProject.id,
          user_ids: this.newInteractionMentions
        })

        if (capabilities.code === 200 && capabilities.user_projects && capabilities.user_projects.length) {
          const participantIds = [
            ...(this.currentTarea.participants || []).map(participant => participant.user_id || participant.id),
            this.currentTarea.created_by,
            this.currentTarea.responsable
          ]
            .filter(userId => userId !== null && userId !== undefined)
            .map(userId => Number(userId))
          const usersWithoutPersonalTasks = capabilities.user_projects
            .filter(userProject => userProject.capabilities?.view_personal_tasks !== true)
            .map(userProject => Number(userProject.user_id))

          this.pendingMentionParticipants = this.newInteractionMentions
            .filter(userId => !participantIds.includes(Number(userId)) && usersWithoutPersonalTasks.includes(Number(userId)))
            .map(userId => ({ id: userId }))

          if (this.pendingMentionParticipants.length > 0) {
            this.acceptRejectModalContext = 'mention_confirmation'
            this.acceptRejectModalData = {
              type: 'warning-pill',
              icon: 'mdi-account-clock-outline',
              title: 'Usuario mencionado no participante',
              content_a: 'Al mencionar a un usuario que no participa en esta tarea, se le agregará temporalmente por 3 días.',
              content_b: '¿Deseas continuar con el envío?'
            }
            this.acceptRejectModal = true
            return false
          }
        }
      }

      this.startInteractionCountdown()
    },
    startInteractionCountdown() {
      this.inCancelTime = true
      this.cancelTimer = 4
      this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
      this.cancelInterval = setInterval(() => {
        this.cancelTimer--
        this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
        if (this.cancelTimer == 0) {
          this.inCancelTime = false
          this.confirmButtonText = 'Enviar'
          clearInterval(this.cancelInterval)
          this.addInteraction()
        }
      }, 1000)
    },
    async addMentionParticipants() {
      if (!this.pendingMentionParticipants.length) return true

      const participantData = this.pendingMentionParticipants.map(participant => ({
        id: participant.id,
        start_date: null,
        limit_date: moment().add(3, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss')
      }))
      let response
      try {
        response = await addParticipant({
          task_id: this.currentTarea.id,
          participants: participantData
        })
      } catch (error) {
        response = null
      }

      if (!response || response.code !== 200) {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'No fue posible agregar temporalmente a los usuarios mencionados.',
          title: 'Error',
          created: new Date(),
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#F4516C'
        })
        return false
      }

      this.$store.consultasStore.commit('setCurrentTarea', response.task)
      this.pendingMentionParticipants = []
      return true
    },
    async addInteraction() {
      this.loading = true
      const biggerFiles = []
      const formData = new FormData()
      formData.append('task_id', this.$store.consultasStore.state.currentTarea.id)
      formData.append('message', this.message)
      formData.append('is_response', this.is_response)
      if (this.draftId) formData.append('draft_id', this.draftId)
      if (this.interactionParent) {
        formData.append('parent_id', this.interactionParent.id)
        formData.append('show_in_feed', this.replyShowInFeed ? 1 : 0)
      }
      this.newInteractionMentions.forEach(m => formData.append('mentions[]', m))
      this.files.forEach(file => {
        if (file && file.size < 1000) formData.append('files[]', file)
        else if (file) biggerFiles.push({
          type: 2,
          payload: { task_id: this.$store.consultasStore.state.currentTarea.id, project_id: this.$store.projectsStore.state.selectedProject.id },
          description: this.$store.consultasStore.state.currentTarea.title,
          total: file.size, uuid: uuidv4(), file
        })
      })

      if (!(await this.addMentionParticipants())) {
        this.loading = false
        return
      }

      const interaction = await addInteraction(formData)
      if (interaction.code === 200) {
        if (biggerFiles.length > 0) biggerFiles.forEach(f => {
          f.payload = { ...f.payload, tr_interaction_id: interaction.interaction, project_id: this.$store.projectsStore.state.selectedProject.id }
          this.$store.globalStore.commit('setFilesToUploadCenter', f)
        })
        this.need_reload = true; this.message = ''; this.files = []; this.filesPreview = []
        this.draftId = null; this.draftCreated = false; this.interactionParent = null; this.replyShowInFeed = true
        this.pendingMentionParticipants = []
        this.showEditor = false
        if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
        this.$store.consultasStore.commit('setCurrentTarea', interaction.task)
      }
      this.loading = false
    },
    closeModalAcceptCancel(status = false) {
      this.acceptRejectModal = false
      const context = this.acceptRejectModalContext
      this.acceptRejectModalContext = null
      this.acceptRejectModalData = null
      if (context === 'task_closure') {
        if (status) this.closeTask()
        return
      }
      if (context === 'mention_confirmation') {
        if (status) this.startInteractionCountdown()
        else this.pendingMentionParticipants = []
      }
    },
    startCloseTasks() {
      this.acceptRejectModalContext = 'task_closure'
      this.acceptRejectModalData = {
        title: '¿Cerrar la tarea Nº ' + this.$store.consultasStore.state.currentTarea.number + '?',
        content_a: 'Por favor confirma si deseas cerrar la tarea indicada.'
      }
      this.acceptRejectModal = true
    },
    async closeTask() {
      this.message += '<p><b>Consulta cerrada por usuario</b></p>'
      this.addInteraction()
      const task = await closeTask({ task_id: this.$store.consultasStore.state.currentTarea.id })
      if (task.code === 200) {
        let t = this.$store.consultasStore.state.currentTarea; t.status = 3
        this.$store.consultasStore.commit('setCurrentTarea', t)
        this.$store.notificationsStore.dispatch('getReminders')
        this.$store.projectsStore.dispatch('getDashboardInfo')
        this.need_reload = true; this.message = ''; this.files = []; this.filesPreview = []
      }
    },
    async minimizeTask(){
      const data = {
        task_id: this.currentTarea.id,
        title: this.currentTarea.title,
        type: this.isPossibleAdditionalMode ? 'Posible Adicional' : 'Consulta/Restricción',
        type_id: 1,
        number: this.currentTarea.number
      }
      const draftId = await this.closeModal()
      this.$store.globalStore.commit('addTaskToTasksHandler', { ...data, draft_id: draftId })
    },
    async getConsultasList() {
      let project_id = this.$store.projectsStore.state.selectedProject?.id || parseInt(localStorage.getItem('selectedProjectID'))
      if (project_id) this.$store.consultasStore.dispatch('getMyTasks', { project_id })
    },
    async getAdditionalsLikelyList() {
      let project_id = this.$store.projectsStore.state.selectedProject?.id || parseInt(localStorage.getItem('selectedProjectID'))
      if (project_id) this.$store.adicionalesStore.dispatch('getAdditionalsLikely', { project_id })
    },
    reloadListing() {
      return this.isPossibleAdditionalMode ? this.getAdditionalsLikelyList() : this.getConsultasList()
    },
    sendToTask() {
      const t = this.$store.consultasStore.state.currentTarea
			const associations = [{ type: 'task', id: t.id, title: t.title, number: t.number }]
      this.$store.consultasStore.commit('setBorradorTarea', { title: t.title, code_id: t.code?.id, description: t.description, task_id: t.id, associations })
      this.$store.consultasStore.commit('setCreateTareaModal', true)
    },
    sendToAdditionalLikely() {
      const t = this.$store.consultasStore.state.currentTarea
			const associations = [{ type: 'task', id: t.id, title: t.title, number: t.number }]
      this.$store.consultasStore.commit('setBorradorTarea', { title: t.title, code_id: t.code?.id, description: t.description, task_id: t.id, associations })
      this.$store.adicionalesStore.commit('setCreatePosibleAdicionalModal', true)
    },
    sendToRDI() {
      const t = this.$store.consultasStore.state.currentTarea
			const associations = [{ type: 'task', id: t.id, title: t.title, number: t.number }]
      this.$store.rdiStore.commit('setInitialDataRDI', { title: t.title, description: t.description, task_id: t.id, associations })
      this.$store.rdiStore.commit('setCreateRDIModal', true)
    },
    sendToAdditional() {
      const t = this.$store.consultasStore.state.currentTarea
			const associations = [{ type: 'task', id: t.id, title: t.title, number: t.number }]
      this.$store.adicionalesStore.commit('setInitialDataAdicional', { title: t.title, description: t.description, task_id: t.id, associations })
      this.$store.adicionalesStore.commit('setCreateAdicionalModal', true)
      this.closeModal()
    },
    async applyChangeResponsable(userID, keepPreviousAsParticipant = false) {
      this.loadingChangeUser = true
      const previousResponsableID = this.$store.consultasStore.state.currentTarea.responsable
      const taskUpdate = await changeResponsable({ task_id: this.$store.consultasStore.state.currentTarea.id, user_id: userID })
      if (taskUpdate.code == 200) {
        let updatedTask = taskUpdate.task
        if (keepPreviousAsParticipant && previousResponsableID && previousResponsableID !== userID && !updatedTask.participants.some(p => p.id === previousResponsableID)) {
          const pu = await addParticipant({ task_id: this.$store.consultasStore.state.currentTarea.id, participants: [previousResponsableID] })
          if (pu.code === 200) updatedTask = pu.task
        }
        this.$store.consultasStore.commit('setCurrentTarea', updatedTask)
        this.reloadListing()
      }
      this.loadingChangeUser = false
    },
    changeUser(userID, promptDowngrade = true) {
      if (this.$store.consultasStore.state.currentTarea.responsable == userID) return
      if (promptDowngrade) { this.pendingResponsableUserID = userID; this.changeResponsablePromptModal = true; return }
      this.applyChangeResponsable(userID, false)
    },
    cancelChangeResponsablePrompt() { this.changeResponsablePromptModal = false; this.pendingResponsableUserID = null },
    async confirmChangeResponsable(keepPreviousAsParticipant) {
      if (!this.pendingResponsableUserID) { this.cancelChangeResponsablePrompt(); return }
      await this.applyChangeResponsable(this.pendingResponsableUserID, keepPreviousAsParticipant)
      this.cancelChangeResponsablePrompt()
    },
    changeResponsableOnly() { this.confirmChangeResponsable(false) },
    changeResponsableKeepingParticipant() { this.confirmChangeResponsable(true) },
    async viewTarea(rdi_id, seen_by_me) {
      this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
      const rdi = await getRdiByID({ rdi_id })
      if (rdi.code === 200) { let d = rdi.rdi; d.prev_seen_by_me = seen_by_me; this.$store.rdiStore.commit('setCurrentRDI', d) }
    },
    async downloadAttachments(attachments) {
      this.loadingFilePath = true
      const files = await getZippedFiles({ files_to_zip: attachments })
      if (files.code == 200) window.open(files.fileurl)
      this.loadingFilePath = false
    },
    closeAddParticipantModal() { this.addParticipantModal = false },
    async removeParticipant(participant) {
      this.loadingRemoveParticipant = true
      const task = await removeParticipant({ user_id: participant.user_id, task_id: this.currentTarea.id })
      this.loadingRemoveParticipant = false
      if (task.code === 200) this.$store.consultasStore.commit('setCurrentTarea', task.task)
    },
    async requestTaskAttribution() {
      if (this.attributionRequested) return
      const task = await requestTaskAttribution({ task_id: this.currentTarea.id })
      if (task.code === 200) this.reloadTask()
    },
    changeStatusTaskAttribution(status, user_id, tasks_responsible_request_id) {
      if (status == 2) this.changeUser(user_id, false)
      changeTaskAttributionRequest({ tasks_responsible_request_id, status })
    },
    async reloadTask() {
			this.isLoading = true
      const tarea = await getTareaByID({ task_id: this.currentTarea.id })
      if (tarea.code === 200) this.$store.consultasStore.commit('setCurrentTarea', tarea.task)
			this.isLoading = false
    },
    onClosureProfileUpdated(task) {
      if (task) this.$store.consultasStore.commit('setCurrentTarea', task)
      else this.reloadTask()
    },
    clearEditorContent() {
      this.message = ''
      this.files = []
      this.filesPreview = []
      this.interactionParent = null
      this.replyShowInFeed = true
    },
    startResize(e) { this.isResizing = true; e.preventDefault() },
    handleResize(e) {
      if (!this.isResizing) return
      const dialog = document.querySelector('.tarea-dialog')
      if (!dialog) return
      const modalBody = dialog.querySelector('.modal-body')
      if (!modalBody) return
      const rect = modalBody.getBoundingClientRect()
      const newLeftWidth = ((e.clientX - rect.left) / rect.width) * 100
      if (newLeftWidth >= 25 && newLeftWidth <= 75) this.leftPanelWidth = newLeftWidth
    },
    stopResize() { if (this.isResizing) this.isResizing = false }
    ,
    bindStatusStripScroll() {
      this.$nextTick(() => {
        this.unbindStatusStripScroll()
        if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mobile) {
          let scrollContainer = this.$refs.topbar || this.$el
          while (scrollContainer && scrollContainer !== document.body && scrollContainer !== document.documentElement) {
            try {
              const style = window.getComputedStyle(scrollContainer)
              const canScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight && /(auto|scroll)/.test(style.overflowY)
              if (canScroll) break
            } catch (e) { void 0 }
            scrollContainer = scrollContainer.parentElement
          }

          if (!scrollContainer || scrollContainer === document.body || scrollContainer === document.documentElement) {
            scrollContainer = this.$el.querySelector('.modal-card')
          }

          if (scrollContainer) {
            this.scrollContainer = scrollContainer
            this.statusScrollables = [scrollContainer]
            scrollContainer.addEventListener('scroll', this.onStatusStripScroll, { passive: true })
            this.onStatusStripScroll()
            return
          }
        }
        this.statusStripSticky = false
      })
    },
    onStatusStripScroll() {
      let anyScrollTop = 0
      try {
        if (this.statusScrollables && this.statusScrollables.length) {
          for (const s of this.statusScrollables) {
            if (!s) continue
            const st = (s === window) ? (window.pageYOffset || document.documentElement.scrollTop || 0) : (s.scrollTop || 0)
            if (st > 0) { anyScrollTop = st; break }
          }
        } else if (this.scrollContainer) {
          const c = this.scrollContainer === window ? window : this.scrollContainer
          anyScrollTop = (c === window) ? (window.pageYOffset || document.documentElement.scrollTop || 0) : (c.scrollTop || 0)
        } else {
          anyScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
        }
      } catch (e) { anyScrollTop = 0 }
      const closed = this.currentTarea && this.currentTarea.status === 3
      // sticky when user scrolled any amount down; removed when at the very top
      const shouldBeSticky = !closed && (anyScrollTop > 0)
      this.statusStripSticky = shouldBeSticky
    },
    unbindStatusStripScroll() {
      if (this.statusScrollables && this.statusScrollables.length) {
        this.statusScrollables.forEach(s => {
          try { s.removeEventListener('scroll', this.onStatusStripScroll) } catch (e) { void 0 }
        })
        this.statusScrollables = []
      }
      try { window.removeEventListener('scroll', this.onStatusStripScroll) } catch (e) { void 0 }
      this.scrollContainer = null
      this.statusStripSticky = false
    }
  }
}
</script>

<style scoped>
/* ─── TOKENS ─────────────────────────────────────────── */
:root {
  --blue: #2D4B95;
  --blue-sec: #5E85EF;
  --blue-light: #EEF2FF;
  --text: #1A1D23;
  --text-muted: #6B7280;
  --border: #E5E8EF;
  --surface: #F8F9FC;
  --radius: 12px;
  --radius-sm: 8px;
}

/* ─── DIALOG ─────────────────────────────────────────── */
.tarea-modal-wrapper::v-deep .tarea-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}
.modal-card {
  border-radius: 16px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}
.text-primary { color: #2D4B95; }

/* ─── TOP BAR ────────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px 16px;
  background: #F4F6FB;
  border-bottom: 1px solid #E0E5F0;
  flex-shrink: 0;
}
.topbar-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.topbar-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: #E8EEFA; border: 1px solid #C8D4F0;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.topbar-titles { flex: 1; min-width: 0; }
.topbar-title {
  font-size: 16px; font-weight: 600; color: #1A1D23;
  margin: 0; line-height: 1.4;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.topbar-number { font-weight: 400; color: #2D4B95; font-size: 14px; }
.topbar-rdi { font-size: 12px; color: #6B7280; margin: 3px 0 0; display: flex; align-items: center; gap: 4px; }
.topbar-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.inline-link-btn {
  background: #E8EEFA; border: none; cursor: pointer;
  border-radius: 50%; width: 20px; height: 20px;
  display: inline-flex; align-items: center; justify-content: center;
}

/* ─── STATUS STRIP ───────────────────────────────────── */
.status-strip {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 10px 24px;
  background: #FAFBFF; border-bottom: 1px solid #E5E8EF;
  flex-wrap: wrap; flex-shrink: 0;
}
.status-strip-pills { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; flex: 1; }
.status-strip-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; margin-left: auto; }
.kanban-column-selector { display: inline-flex; flex-direction: column; align-items: flex-start; gap: 2px; min-width: 0; position: relative; }
.kanban-column-label {
  display: inline-flex; align-items: center; gap: 4px; padding: 0 2px;
  color: #7B8496; font-size: 9px; font-weight: 700; letter-spacing: 0.04em;
  line-height: 1; text-transform: uppercase; white-space: nowrap;
	position: absolute; top: -7px; z-index: 999; left: 8px; background: white;
	border: 1px solid #dbdbdb; border-radius: 5px;
}
.kanban-column-select { width: 210px; max-width: 210px; margin: 0; }
.kanban-column-select::v-deep .v-input__slot {
  height: 35px !important; min-height: 35px !important; padding: 0 8px !important;
  background: #FFFFFF; border: 1px solid #D7DEEC !important;
  border-radius: 7px !important; transition: border-color 0.15s, box-shadow 0.15s;
}
.kanban-column-select::v-deep .v-input__slot:hover { border-color: #9DAED4 !important; }
.kanban-column-select::v-deep.v-input--is-focused .v-input__slot {
  border-color: #2D4B95 !important; box-shadow: 0 0 0 3px rgba(45, 75, 149, 0.1);
}
.kanban-column-select::v-deep .v-select__selection {
  display: flex; align-items: center; max-width: calc(100% - 22px);
}
.kanban-column-select::v-deep .v-input__icon--append .v-icon { color: #2D4B95; }
.kanban-column-option { min-width: 0; }
.kanban-column-option--selection {
  display: inline-flex; align-items: center; gap: 7px;
  max-width: 100%; color: #263A70; font-size: 11px; font-weight: 600;
}
.kanban-column-option--item {
  display: flex; align-items: center; gap: 10px; min-height: 42px;
  padding: 0 12px; color: #374151; font-size: 13px;
  transition: background-color 0.15s, color 0.15s;
}
.kanban-column-option--item:hover { background: #F4F6FB !important; }
.kanban-column-option--active {
  background: #EEF2FF !important; color: #2D4B95;
  font-weight: 600;
}
.kanban-column-option--item .kanban-column-name { flex: 1; }
.kanban-column-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.kanban-column-color {
  width: 10px; height: 10px; flex: 0 0 10px; border-radius: 50%;
  box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 3px rgba(45, 75, 149, 0.12);
}
.tarea-modal-wrapper::v-deep .kanban-column-menu {
  min-width: 230px !important; max-width: min(320px, calc(100vw - 32px));
  padding: 4px 0; border: 1px solid #E5E8EF; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(26, 29, 35, 0.14);
}
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 500; line-height: 1.5;
}
.pill--blue    { background: #E8EEFA; color: #2D4B95; }
.pill--green   { background: #DCFCE7; color: #15803D; }
.pill--orange  { background: #FEF3C7; color: #B45309; }
.pill--gray    { background: #F3F4F6; color: #4B5563; }
.pill--private { background: #F3F4F6; color: #374151; }
.pill--unseen  { background: #FEE2E2; color: #991B1B; }
.pill--seen    { background: #DCFCE7; color: #15803D; }
.pill--priority-high   { background: #FEE2E2; color: #991B1B; }
.pill--priority-medium { background: #FEF3C7; color: #B45309; }
.pill--priority-low    { background: #DCFCE7; color: #15803D; }

/* ─── BODY ───────────────────────────────────────────── */
.modal-body {
  display: flex; flex: 1;
  overflow: hidden; min-height: 0;
}
.modal-body.resizing { cursor: col-resize !important; user-select: none !important; }

/* LEFT panel */
.panel-detail {
  min-width: 0; border-right: none;
  display: flex; flex-direction: column;
  overflow: hidden; flex-shrink: 0;
}
.panel-scroll {
  flex: 1; overflow-y: auto; padding: 20px 24px;
}
.panel-scroll::-webkit-scrollbar { width: 4px; }
.panel-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }

/* RESIZER */
.panel-resizer {
  width: 12px; min-width: 12px; background: transparent;
  cursor: col-resize; flex-shrink: 0; position: relative;
  user-select: none; z-index: 100;
  display: flex; align-items: center; justify-content: center;
}
.panel-resizer::before {
  content: ''; position: absolute; top: 0; left: 5px;
  width: 2px; height: 100%; background: #E5E8EF; transition: background 0.2s;
}
.panel-resizer:hover::before { background: #2D4B95; }
.resize-handle {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 4px; height: 40px; background: white; border-radius: 2px;
  opacity: 0; transition: opacity 0.2s; pointer-events: none;
  box-shadow: 0 0 4px rgba(0,0,0,0.3);
}
.panel-resizer:hover .resize-handle { opacity: 1; }
.resize-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999; cursor: col-resize; user-select: none; background: transparent;
}

/* RIGHT panel */
.panel-interactions {
  display: flex; flex-direction: column;
  overflow: hidden; background: #FAFBFF; flex: 1 1 0; min-width: 0;
}
.panel-interactions--editor-open {
  overflow-y: auto;
}
.panel-interactions--editor-open .interactions-feed {
  flex: none;
  overflow-y: visible;
}
.panel-interactions--editor-open .compose-area {
  max-height: none;
  overflow-y: visible;
}
.interactions-header {
  padding: 14px 18px; border-bottom: 1px solid #E5E8EF;
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; flex-shrink: 0;
}
.interactions-header-actions { display: flex; align-items: center; gap: 8px; }
.interactions-feed { flex: 1; overflow-y: auto; padding: 12px 18px; }
.interactions-feed::-webkit-scrollbar { width: 4px; }
.interactions-feed::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
.empty-interactions {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px; color: #9CA3AF; font-size: 13px;
}
.mobile-add-comment-footer {
  display: flex;
  justify-content: center;
  padding: 12px 0;
  border-top: 1px solid #E5E8EF;
  margin-top: 8px;
}
.mobile-add-comment-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #F0F4FF;
  border: 1px solid #D0DEFF;
  border-radius: 8px;
  color: #2D4B95;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}
.mobile-add-comment-btn:hover {
  background-color: #D0DEFF;
  border-color: #2D4B95;
}
.mobile-add-comment-btn:active {
  background-color: #B8CCFF;
}

/* ─── INTERACTIONS: fix adjuntos empujando meta ──────── */
/* Aplica a cualquier estructura de fila dentro de Interactions */
.interactions-feed::v-deep .interaction-item,
.interactions-feed::v-deep [class*="interaction-row"],
.interactions-feed::v-deep [class*="interaction-card"] {
  min-width: 0;
}

/* El área de adjuntos dentro de cada interacción: columna, no fila */
.interactions-feed::v-deep [class*="interaction-attachments"],
.interactions-feed::v-deep [class*="attachments-section"],
.interactions-feed::v-deep [class*="interaction-files"] {
  width: 100%;
  flex-shrink: 1;
  min-width: 0;
}

/* Meta (fecha, visto por): nunca se achica, se va abajo si no cabe */
.interactions-feed::v-deep [class*="interaction-meta"],
.interactions-feed::v-deep [class*="interaction-footer"],
.interactions-feed::v-deep [class*="interaction-info"] {
  flex-shrink: 0;
  flex-wrap: wrap;
}

/*
 * Patrón más genérico: si el componente Interactions usa un layout flex
 * con adjuntos + meta en la misma fila, forzamos wrap
 */
.interactions-feed::v-deep .d-flex {
  min-width: 0;
}

/* ─── META SECTION ───────────────────────────────────── */
.meta-section { margin-bottom: 20px; }
.meta-two-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.meta-left-column, .meta-right-column { display: flex; flex-direction: column; gap: 12px; }
.meta-item { display: flex; flex-direction: column; gap: 2px; }
.meta-item--full { grid-column: 1 / -1; }
.meta-item--association { display: block; }
.meta-label { font-size: 11px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.04em; }
.meta-value { font-size: 13px; color: #1A1D23; font-weight: 500; }
.meta-responsible { display: flex; align-items: center; gap: 6px; }
.meta-divider { margin: 8px 0 !important; border-color: #E5E8EF !important; }
.meta-label-with-action { display: flex; align-items: center; gap: 4px; margin-bottom: 8px; }
.attribution-requests-list { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.attribution-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px; background: #FEF3C7; border-radius: 6px;
}

/* ─── CONTENT SECTIONS ───────────────────────────────── */
.content-section {
  margin-bottom: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6;
}
.section-title { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 10px; }
.section-header-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.description-body { font-size: 13px; color: #374151; line-height: 1.6; }
::v-deep .description-body p { margin-bottom: 4px; }

/* ─── SUBTAREAS: fila compacta cuando no hay subtareas ── */
.subtasks-empty-row {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ─── SUBTASKS BLOCK (cuando hay subtareas) ──────────── */
.subtasks-block {
  margin-top: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6;
}
.subtasks-block-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
}
.subtasks-block-title-row { display: flex; align-items: center; gap: 6px; }
.subtasks-block-title { font-size: 13px; font-weight: 600; color: #374151; }
.subtasks-count {
  font-size: 11px; font-weight: 600; color: #6B7280;
  background: #E5E8EF; padding: 2px 8px; border-radius: 12px;
}
.subtasks-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
.subtask-card {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 9px 12px; background: #F9FAFB;
  border: 1px solid #E5E8EF; border-radius: 8px;
  cursor: pointer; transition: all 0.15s;
}
.subtask-card:hover { border-color: #2D4B95; background: #F5F7FF; }
.subtask-card.subtask-completed { background: #F0FDF4; border-color: #BBF7D0; }
.subtask-checkbox-wrapper { position: relative; flex-shrink: 0; margin-top: 2px; }
.subtask-check {
  width: 16px; height: 16px; margin: 0; cursor: pointer;
  opacity: 0; position: relative; z-index: 2;
}
.checkbox-custom {
  position: absolute; top: 0; left: 0;
  width: 16px; height: 16px; border: 2px solid #D1D5DB;
  border-radius: 4px; background: white; pointer-events: none; transition: all 0.15s;
}
.subtask-check:checked + .checkbox-custom { background: #2D4B95; border-color: #2D4B95; }
.subtask-check:checked + .checkbox-custom::after {
  content: ''; position: absolute; top: 1px; left: 4px;
  width: 4px; height: 7px; border: solid white;
  border-width: 0 2px 2px 0; transform: rotate(45deg);
}
.subtask-text { font-size: 13px; color: #374151; line-height: 1.5; flex: 1; word-break: break-word; }
.subtask-completed .subtask-text { color: #9CA3AF; text-decoration: line-through; }
.subtask-delete-btn {
  display: none; align-items: center; justify-content: center;
  width: 24px; height: 24px; padding: 0; margin-left: auto;
  background: transparent; border: none; cursor: pointer;
  border-radius: 4px; transition: all 0.15s;
}
.subtask-card:hover .subtask-delete-btn { display: flex; }
.subtask-delete-btn:hover { background: #FEE2E2; }
.subtask-delete-btn:active { transform: scale(0.95); }

/* ─── NEW SUBTASK INPUT ──────────────────────────────── */
.new-subtask-input-wrap { display: flex; flex-direction: column; gap: 6px; }
.new-subtask-input {
  width: 100%;
  border: 1.5px solid #2D4B95;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #1A1D23;
  background: white;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  /* Visual single-line appearance while allowing Shift+Enter for new lines */
  height: 38px;
  min-height: 38px;
  max-height: 38px;
  line-height: 18px;
  resize: none;
  overflow-y: auto;
}
.new-subtask-input::placeholder { color: #9CA3AF; }
.new-subtask-input:focus { border-color: #3B5FDB; box-shadow: 0 0 0 3px rgba(59, 95, 219, 0.1); }
.new-subtask-actions { display: flex; align-items: center; gap: 6px; }
.add-subtask-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border: 1px dashed #D1D5DB; border-radius: 7px;
  background: transparent; font-size: 12px; color: #6B7280;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.add-subtask-btn:hover { border-color: #2D4B95; color: #2D4B95; background: #EEF2FF; }

/* ─── ATTACHMENTS ────────────────────────────────────── */
.attachments-list {
  background: #F9FAFB; border-radius: 8px; overflow: hidden; border: 1px solid #E5E8EF;
}
.attachment-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; font-size: 12px;
}
.attachment-row:not(:last-child) { border-bottom: 1px solid #E5E8EF; }
.attachment-info { display: flex; align-items: center; gap: 6px; min-width: 0; }
.attachment-name { color: #374151; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-actions { display: flex; gap: 4px; flex-shrink: 0; }
.empty-note { font-size: 13px; color: #9CA3AF; margin: 0; }

/* ─── SEARCH ─────────────────────────────────────────── */
.search-wrap {
  display: flex; align-items: center; gap: 6px;
  background: #F3F4F6; border-radius: 8px; padding: 5px 10px;
}
.search-field { border: none; background: transparent; outline: none; font-size: 12px; color: #374151; width: 110px; }
.search-field::placeholder { color: #9CA3AF; }

/* ─── CREATE MENU ────────────────────────────────────── */
.create-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: #2D4B95; color: white; border: none; cursor: pointer;
  border-radius: 8px; padding: 6px 12px; font-size: 12px; font-weight: 600;
  transition: background 0.15s;
}
.create-btn:hover { background: #243d7a; }
.create-menu {
  background: white; border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12); overflow: hidden; min-width: 200px;
}
.create-menu-item {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 14px; border: none; background: transparent;
  font-size: 13px; color: #374151; cursor: pointer; text-align: left; transition: background 0.1s;
}
.create-menu-item:hover { background: #EEF2FF; }

/* ─── COMPOSE AREA ───────────────────────────────────── */
.compose-area {
  border-top: 1px solid #E5E8EF; padding: 12px 16px;
  background: white; flex-shrink: 0; display: flex; flex-direction: column; gap: 8px;
  max-height: 55vh;
  overflow-y: auto;
}
.add-comment-section { display: flex; align-items: center; padding: 8px 0; }
.add-comment-btn {
  display: inline-flex; align-items: center; gap: 8px;
  color: #2D4B95; cursor: pointer; border-radius: 8px;
  font-size: 13px; font-weight: 600; transition: all 0.15s;
}
.add-comment-btn:hover { background: #EEF2FF; }
.reply-banner {
  display: flex; align-items: flex-start; justify-content: space-between;
  background: #EEF2FF; border-left: 3px solid #2D4B95;
  border-radius: 0 6px 6px 0; padding: 8px 10px;
}
.reply-preview {
  font-size: 11px; color: #9CA3AF; max-height: 18px; overflow: hidden;
  white-space: nowrap; text-overflow: ellipsis; max-width: 260px;
}
.reply-preview * { display: inline; }
.response-alert {
  display: flex; align-items: center; gap: 6px; font-size: 12px;
  color: #B45309; background: #FEF3C7; border-radius: 6px; padding: 6px 10px;
}
.inline-link { background: none; border: none; color: #2D4B95; cursor: pointer; font-size: 12px; text-decoration: underline; padding: 0; }
.compose-actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.send-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #2D4B95; color: white; border: none; cursor: pointer;
  border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600;
  transition: background 0.15s;
}
.send-btn:hover:not(:disabled) { background: #243d7a; }
.send-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.compose-secondary { display: flex; align-items: center; gap: 6px; }
.task-closed-notice {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #15803D; flex-shrink: 0;
}

/* ─── BUTTONS ────────────────────────────────────────── */
.icon-action-btn {
  width: 28px; height: 28px; border-radius: 7px;
  background: #EEF2FF; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; transition: background 0.12s;
}
.icon-action-btn:hover { background: #dde5f9; }
.icon-action-btn.success { background: #DCFCE7; }
.icon-action-btn.success:hover { background: #BBF7D0; }
.icon-action-btn.danger  { background: #FEE2E2; }
.icon-action-btn.danger:hover  { background: #FECACA; }
.text-action-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: 1px solid #E5E8EF; cursor: pointer;
  border-radius: 7px; padding: 5px 10px; font-size: 12px; color: #374151; transition: background 0.12s;
}
.text-action-btn:hover { background: #F3F4F6; }

/* ─── ATTACHMENTS PREVIEW ────────────────────────────── */
.attachments-preview { display: flex; flex-direction: column; gap: 6px; }
.attach-toggle {
  display: inline-flex; align-items: center; gap: 5px;
  background: #F3F4F6; border: 1px solid #E5E8EF; border-radius: 7px;
  padding: 4px 10px; font-size: 12px; color: #6B7280; cursor: pointer;
}
.attach-dot { width: 6px; height: 6px; border-radius: 50%; background: #2D4B95; flex-shrink: 0; }
.chevron { transition: transform 0.15s; }
.chevron.open { transform: rotate(180deg); }
.attach-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.img-chip {
  position: relative; width: 56px; height: 56px; border-radius: 8px;
  overflow: hidden; border: 1px solid #E5E8EF; flex-shrink: 0;
}
.img-thumb { width: 100%; height: 100%; object-fit: cover; }
.chip-remove {
  position: absolute; top: 2px; right: 2px; width: 16px; height: 16px;
  border-radius: 50%; background: rgba(0,0,0,0.5); border: none;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.file-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 8px; border: 1px solid #E5E8EF; border-radius: 7px;
  background: #F9FAFB; font-size: 12px; max-width: 180px;
}
.file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.chip-remove.file {
  position: static; width: 16px; height: 16px; background: #E5E8EF;
  border-radius: 50%; border: none; display: flex; align-items: center;
  justify-content: center; cursor: pointer; flex-shrink: 0;
}

/* ─── LOADING ─────────────────────────────────────────── */
.loading-card { min-height: 180px; }
.loading-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; background: #F4F6FB; border-bottom: 1px solid #E0E5F0;
}
.modal-brand { display: flex; align-items: center; gap: 10px; }
.brand-icon-dark { width: 32px; height: 32px; border-radius: 8px; background: #E8EEFA; border: 1px solid #C8D4F0; display: flex; align-items: center; justify-content: center; }
.brand-label-dark { font-size: 15px; font-weight: 600; color: #1A1D23; }
.close-btn-dark {
  width: 32px; height: 32px; border-radius: 8px; background: #EEEFF2; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s;
}
.close-btn-dark:hover { background: #E0E1E5; }
.loading-body { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; gap: 12px; }
.loading-text { font-size: 13px; color: #6B7280; margin: 0; }

/* ─── MENUS ───────────────────────────────────────────── */
.responsible-menu { border-radius: 10px !important; overflow: hidden; width: 280px !important; }
.user-avatar-sm { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* ─── SEARCH RESPONSABLE ───────────────────────────────── */
.search-responsible-wrapper {
  position: relative;
  padding: 8px 12px;
  border-bottom: 1px solid #E5E8EF;
}
.search-responsible-wrapper::v-deep .v-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.search-responsible-input {
  width: 100%;
  border: 1px solid #E5E8EF;
  border-radius: 6px;
  padding: 8px 12px 8px 32px;
  font-size: 12px;
  outline: none;
  font-family: inherit;
}
.search-responsible-input:focus {
  border-color: #2D4B95;
  background-color: #F8F9FC;
}
.search-responsible-input::placeholder {
  color: #999;
}

/* ─── MOBILE ──────────────────────────────────────────── */
@media (max-width: 768px) {
  /* La tarjeta es el contenedor de scroll → status-strip fluye y scrollea */
  .modal-card {
    border-radius: 0 !important;
    max-height: 100vh; height: 100vh;
    overflow-y: auto; overflow-x: hidden;
  }

  /* Topbar fijo arriba al hacer scroll */
  .topbar {
    position: sticky; top: 0; z-index: 20;
    isolation: isolate;
    flex-direction: column; align-items: stretch;
    padding: 12px 16px; gap: 6px;
  }
  .topbar-right { order: -1; justify-content: flex-end; }
  .topbar-left { order: 0; }

  /* modal-body NO es scroll container en móvil */
  .modal-body {
    flex-direction: column;
    overflow: visible;
    min-height: unset;
  }

  .panel-detail {
    width: 100% !important; flex: none;
    border-right: none; border-bottom: 1px solid #E5E8EF;
    overflow: visible;
  }
  .panel-scroll { overflow: visible; padding: 14px 16px; flex: none; height: auto; }
  .panel-resizer { display: none; }

  .panel-interactions {
    width: 100% !important; flex: none; background: white;
    min-height: 400px; overflow: visible;
  }
  .interactions-feed { overflow: visible; min-height: 200px; padding: 12px 16px; flex: none; }

  /* Adjuntos en interacciones: en mobile el contenedor de cada interacción
     pasa a columna para que los adjuntos no empujen la meta a la derecha */
  .interactions-feed::v-deep [class*="interaction-header"],
  .interactions-feed::v-deep [class*="interaction-top"],
  .interactions-feed::v-deep [class*="message-header"] {
    flex-wrap: wrap !important;
  }
  .interactions-feed::v-deep [class*="interaction-attachments"],
  .interactions-feed::v-deep [class*="attachments-section"],
  .interactions-feed::v-deep [class*="interaction-files"],
  .interactions-feed::v-deep [class*="files-section"] {
    width: 100% !important;
    order: 10;
    margin-top: 6px;
  }
  .interactions-feed::v-deep [class*="interaction-meta"],
  .interactions-feed::v-deep [class*="interaction-footer"],
  .interactions-feed::v-deep [class*="interaction-info"],
  .interactions-feed::v-deep [class*="message-meta"] {
    flex-shrink: 0 !important;
    order: 5;
  }

  /* Columnas meta: mantener 2 columnas */
  .meta-two-columns { grid-template-columns: 1fr 1fr; gap: 12px; }

  .status-strip { padding: 8px 16px; flex-direction: column; align-items: flex-start; }
  .status-strip-actions { width: 100%; justify-content: flex-end; flex-wrap: wrap; }
  .kanban-column-selector { flex: 1 1 100%; align-items: flex-end; }
  .kanban-column-select { width: min(220px, 100%); max-width: 220px; }
  .interactions-header { flex-wrap: wrap; gap: 8px; padding: 12px 16px; }
  .interactions-header-actions { width: 100%; justify-content: flex-end; }
  .search-field { width: 100%; }
  .search-wrap { flex: 1; }
  .compose-area { padding: 10px 14px; }

  .status-strip--sticky {
    position: sticky;
    z-index: 9;
    background: #FAFBFF;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.07);
  }
	.status-strip--sticky .status-strip-pills{
		display: none;
	}
}
</style>